import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { collectTicketDeposit, type CollectDepositInput } from '@/lib/api/tickets.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

type DepositMethod = CollectDepositInput['method'];

export function CollectDepositModal({
  open,
  ticketId,
  ticketNumber,
  maxAmount,
  customerHasStoreCredit,
  onClose,
  onSuccess,
  variant = 'default',
  showManagerOverride = false,
  onManagerSkip,
  successToastMessage,
}: {
  open: boolean;
  ticketId: string;
  ticketNumber: string;
  maxAmount: number;
  customerHasStoreCredit?: boolean;
  onClose: () => void;
  onSuccess: () => void;
  variant?: 'default' | 'mandatory_intake';
  showManagerOverride?: boolean;
  /** Return false / throw if the skip was cancelled (e.g. manager PIN dismissed). */
  onManagerSkip?: () => Promise<boolean | void>;
  successToastMessage?: string;
}) {
  const [amount, setAmount] = useState(String(maxAmount));
  const [method, setMethod] = useState<DepositMethod>('cash');
  const [cashTendered, setCashTendered] = useState('');
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);

  const mandatory = variant === 'mandatory_intake';

  useEffect(() => {
    if (open) {
      setAmount(String(maxAmount));
      setMethod('cash');
      setCashTendered('');
      setSkipConfirmOpen(false);
    }
  }, [open, maxAmount]);

  const amountValue = Math.min(Math.max(Number(amount) || 0, 0), maxAmount);
  const cashTenderedValue = Number(cashTendered || amountValue);
  const changeDue = method === 'cash' ? Math.max(cashTenderedValue - amountValue, 0) : 0;

  const mutation = useMutation({
    mutationFn: (body: CollectDepositInput) => collectTicketDeposit(ticketId, body),
    onSuccess: (result) => {
      const msg =
        successToastMessage ??
        `Deposit of ${formatCurrency(result.payment.amount)} collected`;
      toastSuccess(msg);
      if (result.cashSessionWarning) toastError(result.cashSessionWarning);
      onSuccess();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const skipMutation = useMutation({
    mutationFn: async () => {
      if (!onManagerSkip) return false;
      const ok = await onManagerSkip();
      return ok !== false;
    },
    onSuccess: (ok) => {
      if (ok) setSkipConfirmOpen(false);
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const methods = useMemo(() => {
    const list: DepositMethod[] = ['cash', 'card'];
    if (customerHasStoreCredit) list.push('store_credit');
    return list;
  }, [customerHasStoreCredit]);

  if (!open) return null;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (amountValue <= 0) {
      toastError('Enter a deposit amount');
      return;
    }
    mutation.mutate({
      amount: amountValue,
      method,
      ...(method === 'cash' ? { cashTendered: cashTenderedValue } : {}),
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="collect-deposit-title"
      onClick={mandatory ? undefined : onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="collect-deposit-title" className="text-lg font-bold text-slate-950">
              {mandatory ? '⚠ Deposit Required to Complete Intake' : 'Collect Deposit'}
            </h2>
            <p className="text-sm text-slate-500">{ticketNumber}</p>
            {mandatory ? (
              <p className="mt-2 text-sm font-semibold text-amber-900">
                Amount required: {formatCurrency(maxAmount)}
              </p>
            ) : null}
          </div>
          {mandatory ? null : (
            <button
              type="button"
              className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <form className="space-y-4" onSubmit={submit}>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Amount</span>
            <input
              type="number"
              min={0.01}
              max={maxAmount}
              step="0.01"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <span className="mt-1 block text-xs text-slate-500">
              Up to {formatCurrency(maxAmount)} remaining
            </span>
          </label>

          <div>
            <span className="text-sm font-medium text-slate-700">Payment method</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {methods.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={[
                    'rounded-xl px-4 py-2 text-sm font-semibold capitalize',
                    method === m ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700',
                  ].join(' ')}
                  onClick={() => setMethod(m)}
                >
                  {m.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {method === 'cash' ? (
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Cash tendered</span>
              <input
                type="number"
                min={amountValue}
                step="0.01"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={cashTendered || String(amountValue)}
                onChange={(e) => setCashTendered(e.target.value)}
              />
              <span className="mt-1 block text-sm font-semibold text-slate-950">
                Change: {formatCurrency(changeDue)}
              </span>
            </label>
          ) : null}

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-2">
            {mandatory && showManagerOverride && onManagerSkip ? (
              <button
                type="button"
                className="order-last w-full rounded-xl border border-slate-300 px-4 py-2 font-semibold text-slate-700 sm:order-first sm:w-auto"
                disabled={skipMutation.isPending}
                onClick={() => setSkipConfirmOpen(true)}
              >
                Manager Override — Skip Deposit
              </button>
            ) : null}
            {mandatory ? null : (
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 font-semibold text-slate-700"
                onClick={onClose}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"
              disabled={mutation.isPending || amountValue <= 0}
            >
              {mutation.isPending ? 'Collecting…' : 'Collect Deposit'}
            </button>
          </div>
        </form>
      </div>

      {skipConfirmOpen ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="skip-deposit-confirm-title"
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="skip-deposit-confirm-title" className="text-base font-bold text-slate-950">
              Skip deposit collection?
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              This will be noted on the ticket.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                disabled={skipMutation.isPending}
                onClick={() => setSkipConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={skipMutation.isPending}
                onClick={() => skipMutation.mutate()}
              >
                {skipMutation.isPending ? 'Saving…' : 'Yes, Skip — I am a manager'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
